import { useState } from 'react'
import { Table, Anchor, Group, Text, Badge, ActionIcon } from '@mantine/core'
import {
  IconChevronRight,
  IconChevronDown,
  IconAlertTriangle,
} from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { formatMoney } from './formatMoney'
import type { OrgTreeNode } from '@/api/finopsOrgTree'

interface Props {
  data: {
    months: string[]
    currentMonthIsMtd?: boolean
    tree: OrgTreeNode
  }
  sortBy?: 'name' | 'cost'
  search?: string
}

/**
 * Tabela hierárquica ROOT → OU → ACCOUNT para o relatório FinOps Org Tree.
 *
 * - Expand/collapse por nó (ROOT/OU); ACCOUNT é folha.
 * - Custo por mês alinhado por índice com `data.months`; `null` vira em-dash.
 * - MTD parcial: ícone laranja na última coluna quando `currentMonthIsMtd`.
 * - Search: filtra mostrando ancestrais de nós que casam (em qualquer nível).
 * - Sort: `name` (padrão) ou `cost` (descendente pelo último mês).
 * - Conta tem link para `/finops/contas/<id>`.
 */
export function OrgTreeTable({ data, sortBy = 'name', search }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set([data.tree.id]),
  )
  const navigate = useNavigate()
  const months = data.months

  const matchesSearch = (n: OrgTreeNode): boolean => {
    if (!search) return true
    const q = search.toLowerCase()
    if (
      n.name.toLowerCase().includes(q) ||
      n.id.toLowerCase().includes(q)
    ) {
      return true
    }
    return (n.children || []).some(matchesSearch)
  }

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const renderRows = (node: OrgTreeNode, depth: number): React.ReactNode[] => {
    if (!matchesSearch(node)) return []
    const isExpanded = expanded.has(node.id)
    const isAccount = node.type === 'ACCOUNT'
    const isOuOrRoot = node.type === 'OU' || node.type === 'ROOT'
    const rowStyle: React.CSSProperties = isOuOrRoot
      ? {
          fontWeight: 600,
          background: 'var(--ai-bg-elevated)',
        }
      : {}

    let children = node.children || []
    if (sortBy === 'cost') {
      children = [...children].sort(
        (a, b) =>
          (b.costs_usd[months.length - 1] ?? 0) -
          (a.costs_usd[months.length - 1] ?? 0),
      )
    } else {
      // sortBy === 'name'
      children = [...children].sort((a, b) =>
        a.name.localeCompare(b.name, 'pt-BR'),
      )
    }

    const rows: React.ReactNode[] = [
      <Table.Tr key={node.id} style={rowStyle}>
        <Table.Td style={{ paddingLeft: 8 + depth * 16 }}>
          <Group gap="xs" wrap="nowrap">
            {!isAccount ? (
              <ActionIcon
                variant="subtle"
                size="xs"
                onClick={() => toggle(node.id)}
                aria-label={isExpanded ? 'Recolher' : 'Expandir'}
                aria-expanded={isExpanded}
              >
                {isExpanded ? (
                  <IconChevronDown size={14} />
                ) : (
                  <IconChevronRight size={14} />
                )}
              </ActionIcon>
            ) : (
              <span style={{ width: 18, display: 'inline-block' }} />
            )}
            {isAccount ? (
              <Anchor
                onClick={() => navigate(`/finops/contas/${node.id}`)}
                style={{ cursor: 'pointer' }}
              >
                {node.name}
              </Anchor>
            ) : (
              <Text fw={isOuOrRoot ? 600 : 400}>{node.name}</Text>
            )}
          </Group>
        </Table.Td>
        {node.costs_usd.map((c, i) => (
          <Table.Td key={i} ta="right">
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <Text
                span
                c={c == null ? 'dimmed' : undefined}
              >
                {formatMoney(c)}
              </Text>
              {data.currentMonthIsMtd &&
                i === months.length - 1 &&
                c != null && (
                  <IconAlertTriangle
                    size={12}
                    aria-label="MTD parcial"
                    color="orange"
                  />
                )}
            </Group>
          </Table.Td>
        ))}
      </Table.Tr>,
    ]
    if (isExpanded && children.length > 0) {
      for (const ch of children) {
        rows.push(...renderRows(ch, depth + 1))
      }
    }
    return rows
  }

  return (
    <Table striped withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Nome</Table.Th>
          {months.map((m, i) => (
            <Table.Th key={m} ta="right">
              {m}
              {data.currentMonthIsMtd && i === months.length - 1 ? (
                <Badge size="xs" color="orange" ml="xs">
                  MTD parcial
                </Badge>
              ) : null}
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{renderRows(data.tree, 0)}</Table.Tbody>
    </Table>
  )
}
